import React, { useRef, useEffect, useState } from 'react';
import { Modal, Button, Space } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import EmailEditor from 'react-email-editor';

const MERGE_TAGS = {
  first_name: { name: 'First Name', value: '{{first_name}}', sample: 'John' },
  full_name:  { name: 'Full Name',  value: '{{full_name}}',  sample: 'John Smith' },
  email:      { name: 'Email',      value: '{{email}}',      sample: 'john@example.com' },
  city:       { name: 'City',       value: '{{city}}',       sample: 'New York' },
  state:      { name: 'State',      value: '{{state}}',      sample: 'NY' },
  country:    { name: 'Country',    value: '{{country}}',    sample: 'US' },
};

/**
 * Reusable Unlayer email builder in a modal.
 * Loads the saved design inside Unlayer's onReady callback (the reliable pattern —
 * avoids races between a "ready" state flag and the editor being usable).
 *
 * Props: open · initialDesign (object|null) · onSave({ html, design }) · onClose() · title
 */
export default function EmailBuilderModal({ open, initialDesign, onSave, onClose, title = 'Design email' }) {
  const editorRef = useRef(null);
  const designRef = useRef(initialDesign);
  const [saving, setSaving] = useState(false);

  // Keep the latest design available to onReady (which fires after this prop is set).
  useEffect(() => { designRef.current = initialDesign; }, [initialDesign, open]);

  // Unlayer is fully usable here — load the saved design, or start blank for a new email.
  const handleReady = (unlayer) => {
    const ed = unlayer || editorRef.current?.editor;
    if (!ed) return;
    if (designRef.current) {
      try { ed.loadDesign(designRef.current); } catch { /* ignore malformed design */ }
    }
  };

  const handleSave = () => {
    const ed = editorRef.current?.editor;
    if (!ed) { onSave({ html: '', design: null }); return; }
    setSaving(true);
    ed.exportHtml(({ design, html }) => {
      setSaving(false);
      onSave({ html, design });
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={title}
      width="92vw"
      style={{ top: 20, maxWidth: 1200 }}
      destroyOnHidden          // remount the editor each open so onReady fires every time
      maskClosable={false}     // avoid accidental close losing unsaved edits
      footer={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
            Save email
          </Button>
        </Space>
      }
    >
      <div style={{ overflow: 'hidden', height: 640, borderRadius: 10, width: '100%' }}>
        <EmailEditor
          ref={editorRef}
          onReady={handleReady}
          style={{ height: 684, minWidth: 0 }}
          options={{
            displayMode: 'email',
            mergeTags: MERGE_TAGS,
            features: { stockImages: { enabled: true }, userUploads: true },
            tools: { video: { enabled: false } },
            appearance: { theme: 'light', panels: { tools: { dock: 'right' } } },
          }}
        />
      </div>
    </Modal>
  );
}
